// scan/CreatorManager.js — Creators manager (behind the ⚙ next to the creator dropdown).
// Merge, rename, add are the real work; active / inactive is a secondary control.
//   - Merge: videos follow the kept name through hov_creator_aliases (server), already-exported
//     files keep their names (hov_status is never touched).
//   - Rename: the display name is what lands in exported filenames.
//   - Add: by hand, optionally attached to a Drive folder (folder correction).
//   - Duplicate suggestions come from the server; "Not the same" persists.
//   - "Not a creator" hides a name from the dropdown, never deletes a video. Nothing deletes a row.
// Every destructive action can be undone for ~8 seconds from the toast.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { canonKey } from '../creators';
import '../shared/layout.css';
import './CreatorManager.css';

const API = process.env.REACT_APP_API_URL || '';
const UNDO_MS = 8000;
const FILTERS = [['all', 'All'], ['active', 'Active'], ['inactive', 'Inactive'], ['not_creator', 'Not a creator']];

function apiError(err) {
  return (err && err.response && err.response.data && (err.response.data.details || err.response.data.error)) || (err && err.message) || 'Request failed';
}
function shortDate(iso) { return iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'; }
function clip(s, n) { s = String(s || ''); return s.length > n ? s.slice(0, n - 1) + '…' : s; }

export default function CreatorManager({ rows, onClose, onChanged }) {
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(() => new Set());
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState('');
  const [dups, setDups] = useState([]);
  const [sheet, setSheet] = useState(null);          // { kind: 'merge' | 'add', ... }
  const [toast, setToast] = useState(null);          // { text, undo?, error? }
  const [busy, setBusy] = useState(false);
  const toastTimer = useRef(null);

  const loadDups = useCallback(() => {
    axios.get(API + '/api/hov/creators/duplicates', { timeout: 30000 })
      .then((r) => setDups(r.data.pairs || []))
      .catch((e) => console.warn('[creators] duplicates unavailable', apiError(e)));
  }, []);
  useEffect(() => { loadDups(); }, [loadDups]);
  useEffect(() => () => clearTimeout(toastTimer.current), []);
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') { if (sheet) setSheet(null); else if (!editing) onClose(); } };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [sheet, editing, onClose]);

  const live = rows.filter((c) => c.status !== 'merged');
  const counts = { active: 0, inactive: 0, not_creator: 0 };
  live.forEach((c) => { counts[c.status] = (counts[c.status] || 0) + 1; });
  const qk = canonKey(q);
  const shown = live.filter((c) => (filter === 'all' || c.status === filter)
    && (!qk || c.creator_key.indexOf(qk) !== -1 || canonKey(c.display_name).indexOf(qk) !== -1));
  const byKey = {}; rows.forEach((c) => { byKey[c.creator_key] = c; });

  function showToast(text, undo, error) {
    clearTimeout(toastTimer.current);
    setToast({ text, undo, error });
    toastTimer.current = setTimeout(() => setToast(null), undo ? UNDO_MS : 5000);
  }
  async function run(fn, okText, undo) {
    setBusy(true);
    try {
      const out = await fn();
      showToast(typeof okText === 'function' ? okText(out) : okText, undo);
      onChanged(); loadDups();
      return out;
    } catch (e) {
      showToast(apiError(e), null, true);
      return null;
    } finally { setBusy(false); }
  }
  async function doUndo() {
    const t = toast; if (!t || !t.undo) return;
    clearTimeout(toastTimer.current); setToast(null);
    try { await t.undo(); showToast('Undone'); } catch (e) { showToast('Undo failed: ' + apiError(e), null, true); }
    onChanged(); loadDups();
  }

  const setStatus = (c, status, label) => run(
    () => axios.post(API + '/api/hov/creators/' + encodeURIComponent(c.creator_key), { status }, { timeout: 15000 }),
    label, () => axios.post(API + '/api/hov/creators/' + encodeURIComponent(c.creator_key), { status: c.status }, { timeout: 15000 }));

  function startRename(c) { setEditing(c.creator_key); setDraft(c.display_name); }
  function saveRename(c) {
    const v = draft.trim(); setEditing(null);
    if (!v || v === c.display_name) return;
    run(() => axios.post(API + '/api/hov/creators/' + encodeURIComponent(c.creator_key), { display_name: v }, { timeout: 15000 }),
      `Renamed — future exports use “${v}”`,
      () => axios.post(API + '/api/hov/creators/' + encodeURIComponent(c.creator_key), { display_name: c.display_name }, { timeout: 15000 }));
  }

  function merge(fromKeys, into) {
    const from = fromKeys.filter((k) => k !== into);
    const moved = from.reduce((s, k) => s + ((byKey[k] && byKey[k].total) || 0), 0);
    setSheet(null); setSel(new Set());
    return run(() => axios.post(API + '/api/hov/creators/merge', { from, into }, { timeout: 30000 }),
      `${moved.toLocaleString('en-US')} video${moved === 1 ? '' : 's'} moved to “${(byKey[into] || {}).display_name || into}”`,
      () => axios.post(API + '/api/hov/creators/unmerge', { keys: from }, { timeout: 30000 }));
  }
  function dismiss(p) {
    setDups((d) => d.filter((x) => !(x.a === p.a && x.b === p.b)));
    run(() => axios.post(API + '/api/hov/creators/dismiss', { a: p.a, b: p.b }, { timeout: 15000 }),
      'Dismissed — won’t be suggested again',
      () => axios.post(API + '/api/hov/creators/dismiss', { a: p.a, b: p.b, undo: true }, { timeout: 15000 }));
  }
  async function add(name, folder) {
    const out = await run(() => axios.post(API + '/api/hov/creators', { display_name: name, folder_id: folder ? folder.folder_id : null }, { timeout: 60000 }),
      (r) => `“${name}” added` + (r && r.data && r.data.folder ? ` · ${r.data.folder.videos || 0} videos from “${folder.folder_name}”` : ''),
      () => axios.post(API + '/api/hov/creators/' + encodeURIComponent(canonKey(name)) + '/undo-add', { folder_id: folder ? folder.folder_id : null }, { timeout: 60000 }));
    if (out) setSheet(null);
  }

  function toggle(k) { setSel((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; }); }
  const selRows = [...sel].map((k) => byKey[k]).filter(Boolean);

  return (
    <div className="cm-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cm" role="dialog" aria-label="Creators">
        <div className="tb-card-header cm-head">
          <span className="tb-card-ico">◍</span>
          <h2 className="tb-card-title">Creators</h2>
          <span className="cm-counts">{counts.active} active · {counts.inactive} inactive · {counts.not_creator} not a creator</span>
          <button type="button" className="tb-ask-ic" onClick={onClose} aria-label="Close" title="Close">✕</button>
        </div>
        <div className="cm-body">
          <div className="cm-tools">
            <input className="cm-search" placeholder="Filter names…" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
            <div className="hov-seg">
              {FILTERS.map((f) => <button key={f[0]} className={filter === f[0] ? 'on' : ''} onClick={() => setFilter(f[0])}>{f[1]}</button>)}
            </div>
            <button type="button" className="cm-btn pri" onClick={() => setSheet({ kind: 'add' })}>+ Add creator</button>
          </div>

          {dups.length > 0 && (
            <div className="cm-dups">
              <div className="h">Possible duplicates — {dups.length}</div>
              {dups.map((p) => (
                <div className="cm-dup" key={p.a + '|' + p.b}>
                  <span><b>{p.a_name}</b> ({p.a_total}) and <b>{p.b_name}</b> ({p.b_total})</span>
                  <span className="why">{p.reason}</span>
                  <span className="sp">
                    <button type="button" className="cm-btn" disabled={busy} onClick={() => dismiss(p)}>Not the same</button>
                    <button type="button" className="cm-btn pri" disabled={busy} onClick={() => setSheet({ kind: 'merge', keys: [p.a, p.b], into: p.into })}>Merge into {clip(p.b_name, 16)}</button>
                  </span>
                </div>
              ))}
            </div>
          )}

          {selRows.length >= 2 && (
            <div className="cm-bulk">
              <span><b>{selRows.length}</b> creators selected</span>
              <span className="sp">
                <button type="button" className="cm-btn" onClick={() => setSel(new Set())}>Cancel</button>
                <button type="button" className="cm-btn pri" onClick={() => {
                  const keep = selRows.slice().sort((a, b) => (b.total || 0) - (a.total || 0))[0];
                  setSheet({ kind: 'merge', keys: selRows.map((c) => c.creator_key), into: keep.creator_key });
                }}>Merge…</button>
              </span>
            </div>
          )}

          <div className="cm-list">
            {shown.length === 0 && <div className="cm-none">No match.</div>}
            {shown.map((c) => {
              const isSel = sel.has(c.creator_key);
              return (
                <div key={c.creator_key} className={'cm-row' + (isSel ? ' sel' : '') + (c.status === 'not_creator' ? ' gone' : '')}>
                  <button type="button" className="cm-cb" onClick={() => toggle(c.creator_key)} aria-label={isSel ? 'Unselect' : 'Select for merge'}>{isSel ? '✓' : ''}</button>
                  <span className="cm-who">
                    <span className="n">
                      {editing === c.creator_key
                        ? <input className="cm-ed" value={draft} autoFocus onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') saveRename(c); if (e.key === 'Escape') { e.stopPropagation(); setEditing(null); } }}
                            onBlur={() => saveRename(c)} />
                        : <span className="nm">{c.display_name}</span>}
                      <span className={'cm-pill ' + c.status}>{c.status === 'not_creator' ? 'not a creator' : c.status}</span>
                      {(c.aliases || []).map((a) => <span key={a.key} className="cm-pill ali" title="Merged into this creator — its folders follow automatically">was: {a.name}</span>)}
                    </span>
                    <span className="s">{(c.total || 0).toLocaleString('en-US')} video{c.total === 1 ? '' : 's'} · {c.pending || 0} to process · last {shortDate(c.last_upload)}</span>
                  </span>
                  <span className="cm-acts">
                    <button type="button" className="cm-ico" title="Rename" onMouseDown={(e) => e.preventDefault()} onClick={() => (editing === c.creator_key ? saveRename(c) : startRename(c))}>{editing === c.creator_key ? '✓' : '✎'}</button>
                    {c.status !== 'not_creator' && (
                      <button type="button" className="cm-ico" disabled={busy} title={c.status === 'active' ? 'Mark inactive' : 'Mark active'}
                        onClick={() => setStatus(c, c.status === 'active' ? 'inactive' : 'active', `“${c.display_name}” is now ${c.status === 'active' ? 'inactive' : 'active'}`)}>◐</button>
                    )}
                    {c.status === 'not_creator'
                      ? <button type="button" className="cm-ico" disabled={busy} title="Back in the dropdown" onClick={() => setStatus(c, 'active', `“${c.display_name}” is back in the dropdown`)}>↺</button>
                      : <button type="button" className="cm-ico danger" disabled={busy} title="Not a creator — hides it from the dropdown, keeps its videos"
                          onClick={() => setStatus(c, 'not_creator', `“${c.display_name}” hidden from the dropdown — its ${c.total || 0} videos stay in the index`)}>✕</button>}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="cm-hint">
            Tick two names to merge them, or use a suggestion above. Merging moves every video to the kept name and keeps the
            other one as an alias, so future files from its folders follow automatically; already-exported files keep their names.
            Renaming changes the display name — <b>it is what lands in exported filenames</b>.
          </div>
        </div>

        {sheet && sheet.kind === 'merge' && <MergeSheet keys={sheet.keys} into={sheet.into} byKey={byKey} onCancel={() => setSheet(null)} onMerge={merge} />}
        {sheet && sheet.kind === 'add' && <AddSheet busy={busy} onCancel={() => setSheet(null)} onAdd={add} />}
      </div>

      {toast && (
        <div className={'cm-toast' + (toast.error ? ' err' : '')} role="status">
          <span>{toast.text}</span>
          {toast.undo && <button type="button" onClick={doUndo}>Undo</button>}
        </div>
      )}
    </div>
  );
}

function MergeSheet({ keys, into, byKey, onCancel, onMerge }) {
  const [keep, setKeep] = useState(into);
  const list = keys.map((k) => byKey[k]).filter(Boolean);
  const total = list.reduce((s, c) => s + (c.total || 0), 0);
  return (
    <div className="cm-sheet" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="cm-box">
        <h3>Merge {list.length} creators</h3>
        <p>Every video moves to the name you keep. The other names become aliases, so files arriving in their folders follow automatically.</p>
        <label className="cm-lbl">Keep this name</label>
        <select className="cm-input" value={keep} onChange={(e) => setKeep(e.target.value)}>
          {list.map((c) => <option key={c.creator_key} value={c.creator_key}>{c.display_name} — {c.total || 0} videos</option>)}
        </select>
        <div className="cm-warn">{total.toLocaleString('en-US')} videos will be attached to “{(byKey[keep] || {}).display_name}”. Already-exported files keep the filenames they were given.</div>
        <div className="cm-end">
          <button type="button" className="cm-btn" onClick={onCancel}>Cancel</button>
          <button type="button" className="cm-btn pri" onClick={() => onMerge(keys, keep)}>Merge</button>
        </div>
      </div>
    </div>
  );
}

function AddSheet({ busy, onCancel, onAdd }) {
  const [name, setName] = useState('');
  const [fq, setFq] = useState('');
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => {
      axios.get(API + '/api/hov/folders', { params: { q: fq }, timeout: 20000 })
        .then((r) => setFolders(r.data.folders || []))
        .catch(() => setFolders([]));
    }, 250);
    return () => clearTimeout(t);
  }, [fq]);
  return (
    <div className="cm-sheet" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="cm-box">
        <h3>Add a creator</h3>
        <p>For someone whose folder Timbo hasn’t resolved yet, or to set a name by hand.</p>
        <label className="cm-lbl">Display name</label>
        <input className="cm-input" placeholder="e.g. Sofia Martins" value={name} autoFocus onChange={(e) => setName(e.target.value)}
               onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) onAdd(name.trim(), folder); }} />
        <label className="cm-lbl">Attach a Drive folder (optional)</label>
        <input className="cm-input" placeholder="Search folders…" value={fq} onChange={(e) => setFq(e.target.value)} />
        <div className="cm-folders">
          <div className={'cm-folder' + (!folder ? ' on' : '')} onClick={() => setFolder(null)}>— None for now</div>
          {folders.map((f) => (
            <div key={f.folder_id} className={'cm-folder' + (folder && folder.folder_id === f.folder_id ? ' on' : '')} onClick={() => setFolder(f)}>
              <b>{f.folder_name || f.folder_id}</b><span>{f.n} videos{f.creator_display ? ' · now ' + f.creator_display : ''}</span>
            </div>
          ))}
        </div>
        {folder && <div className="cm-warn">The {folder.n} videos of “{folder.folder_name}” — and future ones — will be attributed to {name.trim() || 'this creator'}.</div>}
        <div className="cm-end">
          <button type="button" className="cm-btn" onClick={onCancel}>Cancel</button>
          <button type="button" className="cm-btn pri" disabled={!name.trim() || busy} onClick={() => onAdd(name.trim(), folder)}>Add</button>
        </div>
      </div>
    </div>
  );
}
